<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Tag from 'primevue/tag';
import { PLAYGROUND_GAMES } from '@/lib/playgroundGames';
import { fetchQuestState } from '@/lib/apiClient';
import { useAuth } from '@/composables/useAuth';

const router = useRouter();
const { token } = useAuth();
const questActive = ref(false);

onMounted(async () => {
  if (!token.value) return;
  try {
    const { state } = await fetchQuestState(token.value);
    questActive.value = state.active;
  } catch {
    questActive.value = false;
  }
});

function openGame(gameId: string, route: string) {
  if (gameId === 'forest-quest' && !questActive.value) return;
  router.push(route);
}
</script>

<template>
  <div class="page">
    <section class="page__hero">
      <h1>Игральня</h1>
      <p>Выбери игру — здесь будут появляться новые развлечения для гостей.</p>
    </section>

    <section class="games">
      <h2 class="games__title">Игры</h2>
      <div class="games__grid">
        <button
          v-for="game in PLAYGROUND_GAMES"
          :key="game.id"
          type="button"
          class="game-tile"
          :class="{ 'game-tile--locked': game.id === 'forest-quest' && !questActive }"
          :style="{ background: game.gradient, borderColor: `${game.accent}33` }"
          :disabled="game.id === 'forest-quest' && !questActive"
          @click="openGame(game.id, game.route)"
        >
          <span class="game-tile__emoji">{{ game.emoji }}</span>
          <strong class="game-tile__name">{{ game.name }}</strong>
          <p class="game-tile__desc">{{ game.description }}</p>
          <div class="game-tile__tags">
            <Tag v-for="tag in game.tags" :key="tag" :value="tag" rounded severity="secondary" />
            <Tag
              v-if="game.id === 'forest-quest' && !questActive"
              value="ждёт старта"
              severity="warn"
              rounded
            />
            <Tag
              v-if="game.id === 'forest-quest' && questActive"
              value="идёт сейчас!"
              severity="success"
              rounded
            />
          </div>
          <span class="game-tile__cta" :style="{ color: game.accent }">
            {{ game.id === 'forest-quest' && !questActive ? 'Заблокировано' : 'Играть →' }}
          </span>
        </button>
      </div>
    </section>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 960px;
}

.page__hero h1 {
  margin: 0 0 0.35rem;
  color: #831843;
}

.page__hero p {
  margin: 0;
  color: #9d174d;
}

.games__title {
  margin: 0 0 1rem;
  color: #831843;
  font-size: 1.1rem;
}

.games__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.game-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 1.35rem;
  border: 2px solid;
  border-radius: 1.25rem;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 8px 24px rgb(219 39 119 / 10%);
}

.game-tile:hover:not(:disabled) {
  transform: translateY(-4px);
  box-shadow: 0 14px 32px rgb(219 39 119 / 16%);
}

.game-tile--locked {
  opacity: 0.72;
  cursor: not-allowed;
}

.game-tile__emoji {
  font-size: 2.75rem;
  line-height: 1;
}

.game-tile__name {
  font-size: 1.25rem;
  color: #831843;
}

.game-tile__desc {
  margin: 0;
  color: #9d174d;
  line-height: 1.5;
  font-size: 0.95rem;
}

.game-tile__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.game-tile__cta {
  margin-top: 0.25rem;
  font-weight: 800;
  font-size: 0.95rem;
}
</style>
